import { Link } from 'react-router-dom';

const lastUpdated = 'September 13, 2026';

export function QuickbooksEspoPrivacyPage() {
  return (
    <div className="container legal-page">
      <div className="legal-page-header">
        <span className="eyebrow">Privacy Policy</span>
        <h1>QuickBooks Online &amp; EspoCRM Integration Privacy Policy</h1>
        <p className="legal-page-sub">Last updated: {lastUpdated}</p>
      </div>

      <div className="legal-page-intro">
        <p>
          This Privacy Policy describes how Bizi Office (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or
          &ldquo;our&rdquo;) collects, uses, and protects data when integrating QuickBooks Online
          with EspoCRM. By connecting your QuickBooks Online account to our EspoCRM integration,
          you agree to the practices described below.
        </p>
      </div>

      <nav className="legal-toc" aria-label="Table of contents">
        <h2>Contents</h2>
        <ul>
          <li><a href="#data-collection">Data Collection</a></li>
          <li><a href="#data-usage">Data Usage</a></li>
          <li><a href="#data-sharing">Data Sharing</a></li>
          <li><a href="#security">Security</a></li>
          <li><a href="#contact">Contact &amp; Changes</a></li>
        </ul>
      </nav>

      <section id="data-collection" className="legal-section">
        <h2>Data Collection</h2>
        <p>
          When you authorize our EspoCRM integration to connect to your QuickBooks Online account,
          we collect only the data necessary to perform the synchronization and automation workflows
          you have requested. This may include:
        </p>
        <ul className="legal-list">
          <li>Company and chart-of-accounts metadata required to map records between systems</li>
          <li>Customer, vendor, and contact records synchronized between QuickBooks Online and EspoCRM</li>
          <li>Invoice, payment, and transaction data that your sync rules route into EspoCRM entities</li>
          <li>OAuth authentication tokens issued by QuickBooks Online to authorize API access</li>
          <li>Sync execution logs and error records generated during automated synchronization runs</li>
        </ul>
        <p>
          We do not collect more data than is required to execute the specific synchronization rules
          you have configured. You control which QuickBooks Online entities and fields are accessed
          by defining field mappings and sync scopes within your EspoCRM integration settings.
        </p>
      </section>

      <section id="data-usage" className="legal-section">
        <h2>Data Usage</h2>
        <p>
          We use the data collected through this integration <strong>exclusively to automate and
          synchronize your internal systems</strong>. Specifically:
        </p>
        <ul className="legal-list">
          <li>
            <strong>Record Synchronization:</strong> Data from QuickBooks Online is mapped and
            synchronized into corresponding EspoCRM entities (accounts, contacts, invoices) so
            that your CRM and accounting records stay aligned without manual entry.
          </li>
          <li>
            <strong>Workflow Automation:</strong> Triggered events in QuickBooks Online (such as a
            new invoice or payment) automatically create or update related records in EspoCRM,
            eliminating duplicate data entry and keeping your team informed.
          </li>
          <li>
            <strong>System Synchronization:</strong> Bidirectional sync rules ensure that changes
            in either system propagate to the other, so that data remains current and consistent
            across your CRM and financial platforms.
          </li>
          <li>
            <strong>Error Handling &amp; Logging:</strong> Sync execution logs are generated for
            each run and are used to diagnose failures, retry failed operations, and ensure
            reliable data delivery between systems.
          </li>
          <li>
            <strong>Support &amp; Maintenance:</strong> If you request assistance, we may review
            sync logs to troubleshoot and resolve issues.
          </li>
        </ul>
        <p>
          We do not use your QuickBooks Online data for marketing, advertising, product
          development, or any purpose unrelated to the synchronization workflows you have
          configured.
        </p>
      </section>

      <section id="data-sharing" className="legal-section">
        <h2>Data Sharing</h2>
        <p>
          <strong>We do not sell, rent, or share your QuickBooks Online data with third
          parties.</strong>
        </p>
        <ul className="legal-list">
          <li>
            Your data is not sold to data brokers, advertisers, or analytics providers.
          </li>
          <li>
            Your data is not shared with third-party marketing or sales platforms.
          </li>
          <li>
            Data is only transmitted between QuickBooks Online and your EspoCRM instance. You choose
            which records sync and where they go; we do not route data to any other destination.
          </li>
          <li>
            We do not use your data to train AI models, build aggregate datasets, or create
            derived analytics for any external audience.
          </li>
        </ul>
        <p>
          The only exception is where required by law, court order, or legal process, in which case
          we will disclose only the minimum data necessary to comply with the legal requirement.
        </p>
      </section>

      <section id="security" className="legal-section">
        <h2>Security</h2>
        <p>
          We take the security of your QuickBooks Online data seriously. The following measures are
          in place:
        </p>
        <ul className="legal-list">
          <li>
            <strong>Encrypted Credential Storage:</strong> Your QuickBooks Online OAuth tokens and
            API keys are encrypted at rest and stored within a secure, unique EspoCRM instance
            dedicated to your environment. Credentials are never stored in plaintext, shared
            between tenants, or exported to external systems.
          </li>
          <li>
            <strong>Dedicated Instance Isolation:</strong> Each EspoCRM integration runs in a
            unique instance with its own credential store, sync engine, and access controls. Your
            synchronization rules and data are isolated from other clients&rsquo; environments.
          </li>
          <li>
            <strong>Encrypted Data Transmission:</strong> All API communication between EspoCRM and
            QuickBooks Online is encrypted in transit using TLS/HTTPS. No data is transmitted over
            unencrypted channels.
          </li>
          <li>
            <strong>Access Controls:</strong> Access to the EspoCRM instance and its integration
            credentials is restricted to authorized personnel with a legitimate need.
            Administrative access is protected by strong authentication and least-privilege
            principles.
          </li>
          <li>
            <strong>Sync Logging:</strong> Synchronization execution logs are retained for
            troubleshooting and audit purposes and are accessible only to authorized users within
            your EspoCRM instance.
          </li>
          <li>
            <strong>No Data Retention Beyond Need:</strong> We do not retain QuickBooks Online data
            beyond what is necessary for the operation of your sync rules. When a sync rule is
            removed or an integration is disconnected, associated cached data and credentials are
            purged from the EspoCRM instance.
          </li>
        </ul>
      </section>

      <section id="contact" className="legal-section">
        <h2>Contact &amp; Changes</h2>
        <p>
          If you have questions about this Privacy Policy or our QuickBooks Online &amp; EspoCRM
          integration practices, please <Link to="/#contact" className="legal-link">contact us</Link>.
        </p>
        <p>
          We may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo; date
          at the top of this page reflects the most recent revision. We encourage you to review this
          page periodically to stay informed about how we protect your data.
        </p>
      </section>

      <div className="legal-page-footer">
        <Link to="/" className="btn btn-ghost">&larr; Back to Home</Link>
      </div>
    </div>
  );
}
